/**
 * ATSScorePage — /resume-mastery/ats-score
 *
 * The employer-fit scorer. User pastes resume text + target JD; AI returns
 * structured scoring across 5 dimensions, keyword match analysis, and
 * specific improvement recommendations.
 *
 * BACKEND: POSTs to /api/v1/resume/score (new endpoint implemented in
 * backend/app/routers/resume.py + backend/app/services/prompts_resume.py).
 *
 * TIER: Free 3/month · All Access unlimited.
 */

import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AssessmentIcon from "@mui/icons-material/Assessment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { scoreResume } from "../api/client";
import { useAuth } from "../hooks/useAuth";
import { getTierAccess } from "../utils/tier";
import { toUserSafeErrorMessage } from "../utils/errorMessage";

// coral via theme.palette.primary — per-product accent deferred (spec D2)

// ---------------------------------------------------------------------------
// Types (match the backend response shape)
// ---------------------------------------------------------------------------

interface DimensionScore {
  name: string;
  score: number; // 0–100
  rationale: string;
  improvements: string[];
}

interface KeywordMatch {
  keyword: string;
  priority: "critical" | "high" | "medium";
  status: "present" | "missing" | "needs_emphasis";
}

interface ResumeScoreResponse {
  overall_score: number; // 0–100
  severity: "Strong Apply" | "Apply with Prep" | "Opportunistic" | "Do Not Apply";
  summary: string;
  dimensions: DimensionScore[];
  keyword_matches: KeywordMatch[];
  agentic_readiness: {
    d1: number; d2: number; d3: number; d4: number; d5: number; d6: number; d7: number;
  };
  top_improvements: string[];
  remaining_evaluations_this_month?: number;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function ATSScorePage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation("pages-core");
  const { user } = useAuth();
  const tierAccess = getTierAccess(user);

  const [resumeText, setResumeText] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ResumeScoreResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!resumeText.trim() || !jobDescription.trim()) {
      setError(t("ATSScorePage.missingInputsError"));
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await scoreResume({
        resume_text: resumeText,
        job_description: jobDescription,
      });
      setResult(data as ResumeScoreResponse);
    } catch (e: any) {
      // The backend tailors the 429 message per case (anonymous daily cap → "...today.
      // Sign in..."; logged-in free → monthly limit). Surface detail.message so the
      // right copy + CTA shows; fall back generically.
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 429) {
        setError(
          (typeof detail === "object" && detail?.message) ||
            t("ATSScorePage.freeLimitReachedError")
        );
      } else {
        setError(toUserSafeErrorMessage(e, t("ATSScorePage.evaluationFailedError")));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setResult(null);
    setError(null);
  };

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh", pb: 10 }}>
      {/* Header */}
      <Box sx={{ background: `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.08)} 0%, ${alpha(theme.palette.primary.light, 0.02)} 100%)`, borderBottom: `1px solid ${alpha(theme.palette.primary.light, 0.15)}`, py: { xs: 4, md: 6 } }}>
        <Container maxWidth="md">
          <Stack direction="row" spacing={1} alignItems="center" mb={3}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/resume-mastery")} size="small" sx={{ color: "text.secondary", textTransform: "none", fontWeight: 500 }}>
              {t("ATSScorePage.resumeMasteryBreadcrumb")}
            </Button>
            <Typography variant="body2" color="text.disabled">›</Typography>
            <Typography variant="body2" color="text.secondary">{t("ATSScorePage.atsScorerBreadcrumb")}</Typography>
          </Stack>
          <Typography variant="h3" fontWeight={500} sx={{ letterSpacing: "-0.02em", mb: 1 }}>
            {t("ATSScorePage.heroTitle")}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 720, lineHeight: 1.65 }}>
            {t("ATSScorePage.heroBody")}
          </Typography>
          <Stack direction="row" spacing={1} mt={2}>
            <Chip
              label={
                tierAccess.canAccessProducts
                  ? t("ATSScorePage.tierAllAccess")
                  : user
                    ? t("ATSScorePage.tierFreeLoggedIn")
                    : t("ATSScorePage.tierFreeAnonymous")
              }
              size="small"
              sx={{ fontWeight: 500, fontSize: "0.7rem", bgcolor: alpha(theme.palette.primary.light, 0.12), color: theme.palette.primary.main }}
            />
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
        {!result ? (
          <>
            {/* Input form */}
            <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 3, p: 3, mb: 3 }}>
              <Typography variant="subtitle1" fontWeight={500} gutterBottom>{t("ATSScorePage.pasteResumeStepTitle")}</Typography>
              <TextField
                multiline
                fullWidth
                minRows={10}
                maxRows={20}
                placeholder={t("ATSScorePage.pasteResumePlaceholder")}
                value={resumeText}
                onChange={(e) => setResumeText(e.target.value)}
                disabled={loading}
                sx={{ "& .MuiOutlinedInput-root": { fontFamily: "ui-monospace, Menlo, monospace", fontSize: "0.82rem" } }}
              />
              <Typography variant="caption" color="text.disabled" sx={{ mt: 1, display: "block" }}>
                {t("ATSScorePage.characterCount", { chars: resumeText.length.toLocaleString() })}
              </Typography>
            </Paper>

            <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 3, p: 3, mb: 3 }}>
              <Typography variant="subtitle1" fontWeight={500} gutterBottom>{t("ATSScorePage.pasteJdStepTitle")}</Typography>
              <TextField
                multiline
                fullWidth
                minRows={8}
                maxRows={20}
                placeholder={t("ATSScorePage.pasteJdPlaceholder")}
                value={jobDescription}
                onChange={(e) => setJobDescription(e.target.value)}
                disabled={loading}
                sx={{ "& .MuiOutlinedInput-root": { fontFamily: "ui-monospace, Menlo, monospace", fontSize: "0.82rem" } }}
              />
              <Typography variant="caption" color="text.disabled" sx={{ mt: 1, display: "block" }}>
                {t("ATSScorePage.characterCount", { chars: jobDescription.length.toLocaleString() })}
              </Typography>
            </Paper>

            {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

            <Stack direction="row" justifyContent="flex-end" spacing={2}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={loading ? undefined : <AssessmentIcon />}
                onClick={handleSubmit}
                disabled={loading || !resumeText.trim() || !jobDescription.trim()}
                sx={{ fontWeight: 500, px: 4 }}
              >
                {loading ? t("ATSScorePage.scoringButtonBusy") : t("ATSScorePage.scoreMyFitButton")}
              </Button>
            </Stack>

            {loading && <LinearProgress sx={{ mt: 3, height: 6, borderRadius: 3, "& .MuiLinearProgress-bar": { bgcolor: theme.palette.primary.light } }} />}

            <Paper elevation={0} sx={{ bgcolor: alpha(theme.palette.info.main, 0.04), border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`, borderRadius: 2, p: 2.5, mt: 4 }}>
              <Stack direction="row" spacing={1.25} alignItems="flex-start">
                <InfoOutlinedIcon sx={{ color: theme.palette.info.dark, fontSize: 18, mt: "2px", flexShrink: 0 }} />
                <Box>
                  <Typography variant="caption" fontWeight={700} sx={{ color: theme.palette.info.dark, letterSpacing: "0.05em", textTransform: "uppercase", display: "block", mb: 0.5 }}>
                    {t("ATSScorePage.howScoringWorksLabel")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                    {t("ATSScorePage.howScoringWorksBody")}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </>
        ) : (
          <ScoreResult result={result} onReset={handleReset} />
        )}
      </Container>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Result display
// ---------------------------------------------------------------------------

function ScoreResult({ result, onReset }: { result: ResumeScoreResponse; onReset: () => void }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation("pages-core");
  const scoreColor = result.overall_score >= 75 ? theme.palette.success.dark : result.overall_score >= 50 ? theme.palette.warning.main : result.overall_score >= 30 ? theme.palette.warning.dark : theme.palette.error.dark;

  return (
    <Stack spacing={3}>
      {/* Overall score */}
      <Paper elevation={0} sx={{ bgcolor: alpha(scoreColor, 0.05), border: `1px solid ${alpha(scoreColor, 0.3)}`, borderRadius: 3, p: 4 }}>
        <Stack direction={{ xs: "column", sm: "row" }} alignItems="center" spacing={3}>
          <Box textAlign={{ xs: "center", sm: "left" }}>
            <Typography variant="caption" fontWeight={700} sx={{ color: scoreColor, letterSpacing: "0.05em", textTransform: "uppercase" }}>{t("ATSScorePage.overallFitScoreLabel")}</Typography>
            <Typography variant="h1" fontWeight={900} sx={{ color: scoreColor, letterSpacing: "-0.03em", lineHeight: 1 }}>{result.overall_score}%</Typography>
            <Chip label={result.severity} size="small" sx={{ fontWeight: 500, mt: 1, bgcolor: alpha(scoreColor, 0.15), color: scoreColor }} />
          </Box>
          <Box flex={1}>
            <Typography variant="body1" sx={{ lineHeight: 1.7 }}>{result.summary}</Typography>
            {result.remaining_evaluations_this_month !== undefined && (
              <Typography variant="caption" color="text.disabled" sx={{ display: "block", mt: 2 }}>
                {t("ATSScorePage.remainingEvaluationsMessage", { count: result.remaining_evaluations_this_month })}
              </Typography>
            )}
          </Box>
        </Stack>
      </Paper>

      {/* Funnel: from a one-shot score into the full tailoring workspace */}
      <Button
        variant="contained"
        color="primary"
        endIcon={<ArrowForwardIcon />}
        onClick={() => navigate("/resume-mastery/workspace")}
        sx={{ alignSelf: "flex-start", fontWeight: 500, textTransform: "none", px: 3 }}
      >
        {t("ATSScorePage.fixGapsInWorkspaceButton")}
      </Button>

      {/* Dimensions */}
      <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 3, p: 3 }}>
        <Typography variant="h6" fontWeight={500} gutterBottom>{t("ATSScorePage.dimensionBreakdownHeading")}</Typography>
        <Stack spacing={2.5} mt={2}>
          {result.dimensions.map((d, i) => (
            <Box key={i}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                <Typography variant="body2" fontWeight={500}>{d.name}</Typography>
                <Typography variant="body2" fontWeight={500} sx={{ color: d.score >= 75 ? theme.palette.success.dark : d.score >= 50 ? theme.palette.warning.main : theme.palette.error.dark }}>{d.score}%</Typography>
              </Stack>
              <LinearProgress variant="determinate" value={d.score} sx={{ height: 6, borderRadius: 3, mb: 1, "& .MuiLinearProgress-bar": { bgcolor: d.score >= 75 ? theme.palette.success.dark : d.score >= 50 ? theme.palette.warning.main : theme.palette.error.dark } }} />
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.55, mb: d.improvements.length > 0 ? 1 : 0 }}>{d.rationale}</Typography>
              {d.improvements.length > 0 && (
                <Stack spacing={0.5} mt={0.5}>
                  {d.improvements.map((imp, j) => (
                    <Typography key={j} variant="caption" sx={{ color: "text.secondary", display: "block", pl: 1.5, borderLeft: `2px solid ${alpha(theme.palette.primary.light, 0.3)}` }}>
                      → {imp}
                    </Typography>
                  ))}
                </Stack>
              )}
            </Box>
          ))}
        </Stack>
      </Paper>

      {/* Agentic 7-domain readiness */}
      <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 3, p: 3 }}>
        <Typography variant="h6" fontWeight={500} gutterBottom>{t("ATSScorePage.agenticReadinessHeading")}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
          {t("ATSScorePage.agenticReadinessSubheading")}
        </Typography>
        <Stack spacing={1.25}>
          {[
            { code: "D1", name: t("ATSScorePage.domainMultiAgentFrameworks"), score: result.agentic_readiness.d1 },
            { code: "D2", name: t("ATSScorePage.domainLlmReasoning"), score: result.agentic_readiness.d2 },
            { code: "D3", name: t("ATSScorePage.domainPythonEngineering"), score: result.agentic_readiness.d3 },
            { code: "D4", name: t("ATSScorePage.domainMlopsDeployment"), score: result.agentic_readiness.d4 },
            { code: "D5", name: t("ATSScorePage.domainToolUseApiIntegration"), score: result.agentic_readiness.d5 },
            { code: "D6", name: t("ATSScorePage.domainSafetyGovernance"), score: result.agentic_readiness.d6 },
            { code: "D7", name: t("ATSScorePage.domainCommunicationDelivery"), score: result.agentic_readiness.d7 },
          ].map((d) => (
            <Stack key={d.code} direction="row" alignItems="center" spacing={2}>
              <Chip label={d.code} size="small" sx={{ minWidth: 40, fontWeight: 500, fontSize: "0.72rem", bgcolor: alpha(theme.palette.primary.light, 0.1), color: theme.palette.primary.main }} />
              <Typography variant="body2" sx={{ minWidth: 180 }}>{d.name}</Typography>
              <Box flex={1}>
                <LinearProgress variant="determinate" value={d.score} sx={{ height: 6, borderRadius: 3, "& .MuiLinearProgress-bar": { bgcolor: d.score >= 75 ? theme.palette.success.dark : d.score >= 50 ? theme.palette.warning.main : theme.palette.error.dark } }} />
              </Box>
              <Typography variant="caption" fontWeight={500} sx={{ minWidth: 40, textAlign: "right" }}>{d.score}%</Typography>
            </Stack>
          ))}
        </Stack>
      </Paper>

      {/* Keyword match */}
      <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 3, p: 3 }}>
        <Typography variant="h6" fontWeight={500} gutterBottom>{t("ATSScorePage.keywordMatchHeading")}</Typography>
        <Stack spacing={1}>
          {result.keyword_matches.map((k, i) => (
            <Stack key={i} direction="row" alignItems="center" spacing={2}>
              <Chip label={k.priority.toUpperCase()} size="small" sx={{ minWidth: 80, fontSize: "0.65rem", fontWeight: 500, bgcolor: k.priority === "critical" ? alpha(theme.palette.error.dark, 0.1) : alpha(theme.palette.info.main, 0.1), color: k.priority === "critical" ? theme.palette.error.dark : theme.palette.info.dark }} />
              <Typography variant="body2" fontWeight={500} sx={{ flex: 1 }}>{k.keyword}</Typography>
              {k.status === "present" ? (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <CheckCircleIcon sx={{ color: theme.palette.success.dark, fontSize: 16 }} />
                  <Typography variant="caption" sx={{ color: theme.palette.success.dark, fontWeight: 500 }}>{t("ATSScorePage.keywordStatusPresent")}</Typography>
                </Stack>
              ) : k.status === "needs_emphasis" ? (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <InfoOutlinedIcon sx={{ color: theme.palette.warning.main, fontSize: 16 }} />
                  <Typography variant="caption" sx={{ color: theme.palette.warning.main, fontWeight: 500 }}>{t("ATSScorePage.keywordStatusNeedsEmphasis")}</Typography>
                </Stack>
              ) : (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <CancelIcon sx={{ color: theme.palette.error.dark, fontSize: 16 }} />
                  <Typography variant="caption" sx={{ color: theme.palette.error.dark, fontWeight: 500 }}>{t("ATSScorePage.keywordStatusMissing")}</Typography>
                </Stack>
              )}
            </Stack>
          ))}
        </Stack>
      </Paper>

      {/* Top improvements */}
      <Paper elevation={0} sx={{ bgcolor: alpha(theme.palette.primary.light, 0.04), border: `1px solid ${alpha(theme.palette.primary.light, 0.3)}`, borderRadius: 3, p: 3 }}>
        <Typography variant="h6" fontWeight={500} gutterBottom>{t("ATSScorePage.topImprovementsHeading")}</Typography>
        <Stack spacing={1.25} mt={2}>
          {result.top_improvements.map((imp, i) => (
            <Stack key={i} direction="row" spacing={1.5} alignItems="flex-start">
              <Chip label={i + 1} size="small" sx={{ minWidth: 28, fontWeight: 500, bgcolor: theme.palette.primary.light, color: "white" }} />
              <Typography variant="body2" sx={{ lineHeight: 1.65, flex: 1 }}>{imp}</Typography>
            </Stack>
          ))}
        </Stack>
      </Paper>

      {/* Actions */}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mt={2}>
        <Button variant="outlined" onClick={onReset} sx={{ fontWeight: 500, borderColor: alpha(theme.palette.primary.light, 0.4), color: theme.palette.primary.main }}>
          {t("ATSScorePage.scoreAnotherResumeButton")}
        </Button>
      </Stack>
    </Stack>
  );
}
